import { getA11ySelector } from '@sg/a11y-helpers';
import { type CustomizationMachineTypes } from '@sg/shared-fe-features-customization';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Flattens modification groups into list rows: a heading and one row per
 * ingredient. Ingredients flow through the list's column grid one card per
 * cell, while headings span the full width.
 *
 * Ingredients on the closing line of a section carry a flag so they can drop
 * their bottom spacing, leaving the next heading's own top spacing to separate
 * the sections.
 *
 * Every row of a section carries that section's ID as its scrollspy target, so
 * viewability reports can resolve the active nav item without measuring.
 */
export function buildCustomizationIngredientRows(params: Params): CustomizationIngredientRow[] {
  const { sections, itemsPerRow } = params;

  return sections.flatMap((section) => buildSectionRows(section, itemsPerRow));
}

export function getCustomizationIngredientRowTargetId(row: CustomizationIngredientRow) {
  return row.targetId;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildSectionRows(
  section: CustomizationIngredientSection,
  itemsPerRow: number,
): CustomizationIngredientRow[] {
  const { group, modifications } = section;

  const rowBase: RowBase = { targetId: group.id, group };

  const headingDataSet = getA11ySelector('customization.modification-group.container', {
    group: group.displayName.toLowerCase(),
  }).props.dataSet;

  const headingRow: CustomizationIngredientRow = { ...rowBase, kind: 'section-heading', key: `${group.id}:heading`, dataSet: headingDataSet }; // prettier-ignore

  const groupName = group.name.toLowerCase();
  const lastRowStartIndex = resolveLastRowStartIndex(modifications.length, itemsPerRow);
  const ingredientRows = modifications.map<CustomizationIngredientRow>((modification, index) => ({
    ...rowBase,
    kind: 'ingredient',
    key: `${group.id}:ingredient:${modification.id}`,
    modification,
    index,
    isInLastRow: index >= lastRowStartIndex,
    dataSet: getA11ySelector('customization.modification-group.item', { group: groupName, index })
      .props.dataSet,
  }));

  return [headingRow, ...ingredientRows];
}

/**
 * Index the closing line of a section starts at, so its cards can drop the
 * spacing that separates lines within the section.
 */
function resolveLastRowStartIndex(count: number, itemsPerRow: number) {
  const columns = Math.max(1, itemsPerRow);
  const remainder = count % columns;

  return count - (remainder === 0 ? columns : remainder);
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  sections: readonly CustomizationIngredientSection[];
  itemsPerRow: number;
};

type RowBase = {
  targetId: string;
  group: CustomizationMachineTypes.ExtendedModifierGroup;
};

type A11yDataSet = Record<string, string | number>;

type CustomizationIngredientSection = {
  group: CustomizationMachineTypes.ExtendedModifierGroup;
  modifications: readonly CustomizationMachineTypes.ProductModification[];
};

export type CustomizationIngredientRow = RowBase &
  (
    | {
        kind: 'section-heading';
        key: string;
        dataSet: A11yDataSet;
      }
    | {
        kind: 'ingredient';
        key: string;
        modification: CustomizationMachineTypes.ProductModification;
        index: number;
        isInLastRow: boolean;
        dataSet: A11yDataSet;
      }
  );
